/**
 * Teams: the org unit (WALLIYO-0131).
 *
 * The same shape as the other Configuration screens — a subject to pick, a
 * table, one Save that is one transaction.
 *
 * The list, then one team's membership. Being on the team and leading it are
 * two columns, because they are two facts: whose job the work is, and whose
 * people these are.
 *
 * Roles are a screen of their own. Arranging who works on what is a smaller
 * authority than deciding what anybody may do: MANAGE_TEAMS is high,
 * MANAGE_ROLES is critical, and one screen cannot be gated on two.
 */

import type { EditTeamRequest, TeamListItem } from "@wallyo/contracts/console/configuration";
import { useMemo, useState } from "react";

import { createTeam, editTeam, getTeam, listStaff, listTeams, saveTeamMembers } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { countLabel, SaveBar } from "../features/FeatureTableView";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import { REASON_MIN_LENGTH } from "../team/team-writes";
import {
  EMPTY_NEW_TEAM,
  EMPTY_TEAMS_DRAFT,
  changedRows,
  createTeamProblem,
  createTeamRequest,
  isChanged,
  leadsAfter,
  pickedRow,
  rowOf,
  saveProblem,
  saveRequest,
  withLeads,
  withLoaded,
  withMember,
  withPickedLead,
  withPickedMember,
  type LoadedMember,
  type NewTeamDraft,
  type TeamsDraft,
} from "./teams-model";

const describeFailure = (error: unknown) => describeConfigFailure(error, {}, "features.stale");

/** A team write: a name already used is the one refusal with its own words. */
const describeTeamFailure = (error: unknown) =>
  describeConfigFailure(error, { name_taken: "teams.nameTaken" } as never, "features.stale");

export function TeamsScreen() {
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [creations, setCreations] = useState(0);

  if (making) {
    return (
      <>
        <PageTop title={t("teams.new")} />
        <div className="subject-bar">
          <button type="button" className="btn quiet small" onClick={() => setMaking(false)}>
            {t("teams.back")}
          </button>
        </div>
        <NewTeam
          onCreated={() => {
            setMaking(false);
            setCreations((count) => count + 1);
          }}
        />
      </>
    );
  }

  if (openTeamId !== null) {
    return <TeamMembership staffTeamId={openTeamId} onBack={() => setOpenTeamId(null)} />;
  }

  return (
    <>
      <PageTop title={t("teams.title")} />
      <TeamsTab key={creations} onOpen={setOpenTeamId} onNew={() => setMaking(true)} />
    </>
  );
}

/** Every team, and the form that makes one. */
function TeamsTab({ onOpen, onNew }: { onOpen: (staffTeamId: string) => void; onNew: () => void }) {
  const { client } = useApi();
  const list = useLoad(() => listTeams(client), "teams");
  const teams = list.state.data?.teams ?? [];
  const editable = list.state.data?.editable ?? false;

  if (list.state.status === "failed") {
    return (
      <LoadFailed
        message={t("teams.loadFailed")}
        retry={t("teams.retry")}
        error={list.state.error}
        onRetry={list.reload}
      />
    );
  }
  if (!list.state.data) return <Loading text={t("teams.loading")} />;

  return (
    <>
      <div className="card subject-bar">
        <p className="meta">{t("teams.explain")}</p>
        <div style={{ flexGrow: 1 }} />
        {editable ? (
          <button type="button" className="btn primary" onClick={onNew}>
            {t("teams.new")}
          </button>
        ) : null}
      </div>
      <div className="card">
        <table className="config-list feature-table">
          <thead>
            <tr>
              <th>{t("teams.col.name")}</th>
              <th>{t("teams.col.purpose")}</th>
              <th>{t("teams.col.lead")}</th>
              <th>{t("teams.col.members")}</th>
              <th>{t("teams.col.status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {teams.map((team) => (
              <TeamRow key={team.staffTeamId} team={team} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
        {teams.length === 0 ? <div className="empty">{t("teams.none")}</div> : null}
      </div>
    </>
  );
}

function TeamRow({ team, onOpen }: { team: TeamListItem; onOpen: (staffTeamId: string) => void }) {
  return (
    <tr className={team.active ? undefined : "refused"}>
      <td>{team.name}</td>
      <td>{team.purpose ?? "—"}</td>
      {/* Nobody leading is a real answer, and a common one on a new team. */}
      <td>
        {team.leadNames.length > 0 ? team.leadNames.join(", ") : <span className="meta">{t("teams.noLead")}</span>}
      </td>
      <td className="mono">{team.memberCount}</td>
      <td>
        <span className={`pill ${team.active ? "ok" : "bad"}`}>
          {t(team.active ? "teams.status.open" : "teams.status.closed")}
        </span>
      </td>
      <td>
        <button type="button" className="btn quiet small" onClick={() => onOpen(team.staffTeamId)}>
          {t("teams.open")}
        </button>
      </td>
    </tr>
  );
}

function NewTeam({ onCreated }: { onCreated: () => void }) {
  const { client } = useApi();
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState<NewTeamDraft>(EMPTY_NEW_TEAM);
  const save = useIntentWrite();

  // Who there is to put on it. The staff list is already an endpoint; a team
  // that does not exist yet has no membership of its own to read.
  const people = useLoad(() => listStaff(client), "staff");
  const staff = (people.state.data?.staffMembers ?? []).filter((each) => each.status === "active");

  const submit = () => {
    save.edited();
    void save.run(
      createTeamProblem(name, reason),
      (idempotencyKey) => createTeam(client, createTeamRequest(name, purpose, reason, draft), { idempotencyKey }),
      onCreated,
    );
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="card subject-bar">
        <label className="subject-uuid">
          <span className="subject-label">{t("teams.new.name")}</span>
          <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="subject-uuid">
          <span className="subject-label">{t("teams.new.purpose")}</span>
          <input type="text" value={purpose} onChange={(event) => setPurpose(event.target.value)} />
        </label>
        <label className="subject-uuid">
          <span className="subject-label">{t("team.reason")}</span>
          <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <button type="submit" className="btn primary" disabled={save.pending}>
          {t("teams.new.create")}
        </button>
        {save.problem ? <span className="subject-problem">{save.problem}</span> : null}
      </div>

      <div className="card">
        <p className="meta">{t("teams.new.people")}</p>
        {people.state.status === "failed" ? (
          <LoadFailed
            message={t("teams.loadFailed")}
            retry={t("teams.retry")}
            error={people.state.error}
            onRetry={people.reload}
          />
        ) : !people.state.data ? (
          <Loading text={t("teams.loading")} />
        ) : (
          <table className="config-list feature-table">
            <thead>
              <tr>
                <th>{t("teams.col.person")}</th>
                <th>{t("teams.col.roles")}</th>
                <th>{t("teams.col.onTeam")}</th>
                <th>{t("teams.col.leads")}</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((person) => {
                const row = pickedRow(person.staffMemberId, draft);
                return (
                  <tr key={person.staffMemberId} className={row.member ? "dirty" : undefined}>
                    <td>{person.name}</td>
                    <td>{person.roles.length > 0 ? person.roles.map((role) => role.name).join(", ") : "—"}</td>
                    <td>
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={t("teams.col.onTeam")}
                        checked={row.member}
                        onChange={(event) => {
                          save.edited();
                          setDraft((current) => withPickedMember(current, person.staffMemberId, event.target.checked));
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={t("teams.col.leads")}
                        checked={row.leads}
                        onChange={(event) => {
                          save.edited();
                          setDraft((current) => withPickedLead(current, person.staffMemberId, event.target.checked));
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {people.state.data && staff.length === 0 ? <div className="empty">{t("teams.noOthers")}</div> : null}
      </div>
      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={onCreated} /> : null}
    </form>
  );
}

/** One team: who is on it, who leads it, and one Save for the whole table. */
function TeamMembership({ staffTeamId, onBack }: { staffTeamId: string; onBack: () => void }) {
  const { client } = useApi();
  const [saves, setSaves] = useState(0);
  const [draft, setDraft] = useState<TeamsDraft>(EMPTY_TEAMS_DRAFT);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const save = useIntentWrite();

  const list = useLoad(() => getTeam(client, staffTeamId), `${staffTeamId}#${saves}`);
  const data = list.state.data;
  const people = useMemo(
    () => (data ? withLoaded(data.members, data.available) : ([] as readonly LoadedMember[])),
    [data],
  );
  const editable = data?.editable ?? false;
  const changed = changedRows(people, draft);

  const clear = () => {
    setDraft(EMPTY_TEAMS_DRAFT);
    setReason("");
    setNotice(null);
  };

  const submit = () => {
    if (!data) return;
    save.edited();
    void save.run(
      saveProblem(people, draft, reason, editable),
      (idempotencyKey) =>
        saveTeamMembers(client, staffTeamId, saveRequest(people, draft, reason, data.team.version), {
          idempotencyKey,
        }),
      ({ saved }) => {
        clear();
        setNotice(countLabel("done", saved));
        setSaves((count) => count + 1);
      },
    );
  };

  if (list.state.status === "failed") {
    return (
      <LoadFailed
        message={t("teams.loadFailed")}
        retry={t("teams.retry")}
        error={list.state.error}
        onRetry={list.reload}
      />
    );
  }
  if (!data) return <Loading text={t("teams.loading")} />;

  const leads = leadsAfter(people, draft);

  return (
    <>
      <TeamHeader
        team={data.team}
        leads={leads}
        editable={editable}
        onBack={onBack}
        onChanged={() => {
          // The team's version moved, so the membership draft is against a
          // version that no longer exists: reload rather than let the next
          // Save be refused as stale.
          clear();
          setSaves((count) => count + 1);
        }}
      />

      {notice ? (
        <div className="banner ok" role="status">
          <span>{notice}</span>
          <button className="btn quiet small" type="button" onClick={() => setNotice(null)}>
            {t("features.close")}
          </button>
        </div>
      ) : null}

      <div className="card">
        <p className="meta">{t("teams.leadHint")}</p>
        <table className="config-list feature-table">
          <thead>
            <tr>
              <th>{t("teams.col.person")}</th>
              <th>{t("teams.col.roles")}</th>
              <th>{t("teams.col.onTeam")}</th>
              <th>{t("teams.col.leads")}</th>
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <MemberRow
                key={person.staffMemberId}
                person={person}
                draft={draft}
                editable={editable}
                onMember={(value) => {
                  setNotice(null);
                  save.edited();
                  setDraft((current) => withMember(current, person, value));
                }}
                onLeads={(value) => {
                  setNotice(null);
                  save.edited();
                  setDraft((current) => withLeads(current, person, value));
                }}
              />
            ))}
          </tbody>
        </table>
        {people.length === 0 ? <div className="empty">{t("teams.noMembers")}</div> : null}
      </div>

      {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={list.reload} /> : null}
      <SaveBar
        count={changed.length}
        reason={reason}
        pending={save.pending}
        problem={save.problem}
        onReason={(next) => {
          save.edited();
          setReason(next);
        }}
        onDiscard={clear}
        onSave={submit}
      />
    </>
  );
}

/**
 * The team itself: its name, what it is for, and whether it is open.
 *
 * Renaming is behind a button rather than always-editable, because this is
 * also the heading you read on your way to the table below it.
 *
 * Closing keeps everybody on it. Nothing is emptied — the audit pins them
 * either way, and a team that is reopened having forgotten who was on it is
 * worse than one that is closed with its people intact.
 */
function TeamHeader({
  team,
  leads,
  editable,
  onBack,
  onChanged,
}: {
  team: TeamListItem;
  leads: readonly string[];
  editable: boolean;
  onBack: () => void;
  onChanged: () => void;
}) {
  const { client } = useApi();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(team.name);
  const [purpose, setPurpose] = useState(team.purpose ?? "");
  const [reason, setReason] = useState("");
  const save = useIntentWrite();

  /** `run` does nothing when the first argument is a message, so it is the guard. */
  const edit = (problem: string | null, body: EditTeamRequest) => {
    save.edited();
    void save.run(
      problem ?? (body.reason.trim().length < REASON_MIN_LENGTH ? t("features.save.reasonNeeded") : null),
      (idempotencyKey) => editTeam(client, team.staffTeamId, body, { idempotencyKey }),
      () => {
        setOpen(false);
        setReason("");
        onChanged();
      },
    );
  };

  const rename = () =>
    edit(name.trim().length === 0 ? t("teams.new.nameNeeded") : null, {
      name: name.trim(),
      purpose: purpose.trim() || null,
      reason: reason.trim(),
      version: team.version,
    });

  // Closing and reopening carry their own reason when none was typed: the
  // button is the whole intent, and making somebody type "closed" to close
  // something is how a reason becomes noise instead of a record.
  const setOpenState = (active: boolean) =>
    edit(null, {
      active,
      reason: reason.trim() || t(active ? "teams.reopenedReason" : "teams.closedReason"),
      version: team.version,
    });

  return (
    <>
      <div className="card subject-bar">
        <button type="button" className="btn quiet small" onClick={onBack}>
          {t("teams.back")}
        </button>
        {open ? (
          <>
            <label className="subject-uuid">
              <span className="subject-label">{t("teams.new.name")}</span>
              <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
            </label>
            <label className="subject-uuid">
              <span className="subject-label">{t("teams.new.purpose")}</span>
              <input type="text" value={purpose} onChange={(event) => setPurpose(event.target.value)} />
            </label>
            <label className="subject-uuid">
              <span className="subject-label">{t("team.reason")}</span>
              <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
            <button type="button" className="btn primary" disabled={save.pending} onClick={rename}>
              {t("roles.save")}
            </button>
            <button
              type="button"
              className="btn quiet"
              onClick={() => {
                setOpen(false);
                setName(team.name);
                setPurpose(team.purpose ?? "");
                setReason("");
              }}
            >
              {t("roles.cancel")}
            </button>
          </>
        ) : (
          <>
            <span className="subject-pill">
              <span className="subject-name">{team.name}</span>
              <span className="subject-sep" aria-hidden="true" />
              <span className="subject-kind-tag">{leads.length > 0 ? leads.join(", ") : t("teams.noLead")}</span>
            </span>
            {team.purpose ? <span className="meta">{team.purpose}</span> : null}
            <span className={`pill ${team.active ? "ok" : "bad"}`}>
              {t(team.active ? "teams.status.open" : "teams.status.closed")}
            </span>
            {editable ? (
              <>
                <button type="button" className="btn quiet small" onClick={() => setOpen(true)}>
                  {t("teams.rename")}
                </button>
                <button
                  type="button"
                  className="btn quiet small"
                  disabled={save.pending}
                  onClick={() => setOpenState(!team.active)}
                >
                  {t(team.active ? "teams.close" : "teams.reopen")}
                </button>
              </>
            ) : null}
          </>
        )}
        {save.problem ? <span className="subject-problem">{save.problem}</span> : null}
      </div>
      {save.error ? <WriteFailureNotice failure={describeTeamFailure(save.error)} onReload={onChanged} /> : null}
    </>
  );
}

function MemberRow(props: {
  person: LoadedMember;
  draft: TeamsDraft;
  editable: boolean;
  onMember: (value: boolean) => void;
  onLeads: (value: boolean) => void;
}) {
  const { person, draft, editable, onMember, onLeads } = props;
  const row = rowOf(person, draft);
  const moved = isChanged(person, draft);
  return (
    <tr className={moved ? "dirty" : undefined}>
      <td>
        {person.name}
        {person.active ? null : <span className="pill bad">{t("teams.gone")}</span>}
        <br />
        <span className="mono meta">{person.uuid}</span>
      </td>
      <td>{person.roles.length > 0 ? person.roles.join(", ") : "—"}</td>
      <td>
        <input
          type="checkbox"
          role="switch"
          aria-label={t("teams.col.onTeam")}
          checked={row.member}
          // Somebody who has left is shown so a lead can take them off, and
          // cannot be put back on.
          disabled={!editable || (!person.active && !row.member)}
          onChange={(event) => onMember(event.target.checked)}
        />
      </td>
      <td>
        <input
          type="checkbox"
          role="switch"
          aria-label={t("teams.col.leads")}
          checked={row.leads}
          disabled={!editable || !person.active}
          onChange={(event) => onLeads(event.target.checked)}
        />
      </td>
      {moved ? null : null}
    </tr>
  );
}
